/**
 * Shared domain / DTO types for the Aperture API.
 *
 * The backend is being built in parallel by a different team, so these shapes
 * are our best-effort reading of docs/02-backend-specification.md. Fields are
 * kept optional/loose where we are unsure, and every list response is read
 * defensively (see api/client.ts `unwrapList`) so the UI degrades gracefully
 * instead of crashing if the real backend differs slightly.
 */

/**
 * SuperAdmin is the only global role left - every other capability
 * (managing an organization, judging or entering a competition) is scoped
 * and resolved via OrganizationMember/Judge/CompetitionParticipant instead
 * of a global flag. Most users hold zero roles here; that's the normal state.
 */
export type Role = 'SuperAdmin';

/** Organization-scoped staff role, held via OrganizationMember - see api/types.ts OrganizationMember. */
export type OrganizationRole = 'OrganizationAdmin' | 'Organizer';

export interface User {
  id: string;
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  roles: Role[];
  organizationId?: string | null;
  isActive?: boolean;
  createdAt?: string;
}

export interface AuthResponse {
  accessToken: string;
  user: User;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  /** Accounts are only created from a competition's own link; the new user joins that competition. */
  competitionId: string;
}

export interface Organization {
  id: string;
  name: string;
  description?: string;
  ownerUserId?: string;
  isActive?: boolean;
  createdAt?: string;
  /** Presigned URL for the org's logo, populated only once one has been uploaded (POST /organizations/{id}/logo). */
  logoUrl?: string | null;
}

/** Matches Aperture.Application.Organizations.Dtos.OrganizationMemberDto exactly. */
export interface OrganizationMember {
  id: string;
  organizationId: string;
  userId: string;
  role: OrganizationRole;
}

/**
 * Backend enums serialize as their raw numeric ordinal (System.Text.Json
 * default - no JsonStringEnumConverter is registered), not as strings. This
 * mirrors Aperture.Domain.Competitions.CompetitionStatus exactly:
 * draft -> scheduled -> active -> review -> judging -> awarding -> completed -> archived.
 */
export const CompetitionStatus = {
  Draft: 0,
  Scheduled: 1,
  Active: 2,
  Review: 3,
  Judging: 4,
  Awarding: 5,
  Completed: 6,
  Archived: 7,
} as const;
export type CompetitionStatus = (typeof CompetitionStatus)[keyof typeof CompetitionStatus];

/**
 * Matches Aperture.Application.Competitions.Dtos.CompetitionDto exactly.
 * `status` only changes via the dedicated lifecycle endpoints
 * (schedule/activate/start-review/start-judging/complete/archive) - it is never a
 * free-form field on create/update requests.
 */
/** Technical limits for uploaded images; null / empty = no limit. Formats are lower-case: jpeg, png, webp. */
export interface ImageRequirements {
  maxFileSizeMb: number | null;
  minLongSidePx: number | null;
  maxLongSidePx: number | null;
  minDpi: number | null;
  requireExif: boolean;
  allowedFormats: string[];
  /** true = a non-compliant image is refused; false = it is kept and flagged so the organizer can reject it later. */
  rejectNonCompliant: boolean;
}

export interface CompetitionLanding {
  id: string;
  name: string;
  description?: string | null;
  status: CompetitionStatus;
  organizationName?: string | null;
  organizationLogoUrl?: string | null;
  logoUrl?: string | null;
  submissionOpensAt?: string | null;
  submissionDeadline?: string | null;
  judgingDeadline?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  isOpenForSignup: boolean;
  isFzsPatronage: boolean;
  /** Time-limited download link of the announcement PDF ("razpis"). */
  announcementUrl?: string | null;
  announcementFileName?: string | null;
  imageRequirements: ImageRequirements;
  categories: { name: string; description?: string | null; maxSubmissionsPerUser: number }[];
}

export interface UpdateCompetitionLandingPayload {
  imageRequirements: ImageRequirements;
}

export interface Competition {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  status: CompetitionStatus;
  submissionOpensAt?: string | null;
  submissionDeadline?: string | null;
  judgingDeadline?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  categories?: Category[];
  /** Whether competitors may self-service request to join this competition (POST /competitions/{id}/participants/request) - see Competition.SetOpenForSignup. */
  isOpenForSignup: boolean;
  /** Whether this competition is held under the patronage of FZS (Fotografska zveza Slovenije) - see Competition.SetFzsPatronage. */
  isFzsPatronage: boolean;
  /** Share (1-100) of the best entries of each category selected for the exhibition; set while awarding. */
  exhibitionPercent?: number | null;
  createdAt?: string;
  /** Name/logo of the owning organization, shown when this competition is selected. */
  organizationName?: string | null;
  organizationLogoUrl?: string | null;
  /** This specific competition's own logo (POST /competitions/{id}/logo) - takes priority over organizationLogoUrl when set. */
  logoUrl?: string | null;
  /** Technical limits for uploaded images (see ImageRequirements). */
  imageRequirements?: ImageRequirements | null;
}

/**
 * Backend enum serializes as its raw numeric ordinal, matching
 * Aperture.Domain.Competitions.ParticipantStatus exactly: Pending=0, Approved=1, Rejected=2.
 */
/** Aperture.Domain.Competitions.PaymentMethod exactly: BankTransfer=1, Card=2. */
export const PaymentMethod = {
  BankTransfer: 1,
  Card: 2,
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const ParticipantStatus = {
  Pending: 0,
  Approved: 1,
  Rejected: 2,
} as const;
export type ParticipantStatus = (typeof ParticipantStatus)[keyof typeof ParticipantStatus];

/**
 * Matches Aperture.Application.Competitions.Dtos.CompetitionContextDto
 * exactly: CompetitionContextDto(bool IsParticipant, bool IsJudge, bool CanManage, ParticipantStatus? ParticipantStatus).
 * The calling user's actual relationship to ONE specific competition -
 * deliberately separate from their platform-wide roles, so a page can show a
 * view scoped to this competition instead of every role the user holds
 * anywhere (see GET /competitions/{id}/my-context). isParticipant is true
 * only once participantStatus is Approved; participantStatus (when set) lets
 * the UI show "request pending"/"request rejected" separately from "not a
 * participant at all" (participantStatus undefined/null).
 */
export interface CompetitionContext {
  isParticipant: boolean;
  isJudge: boolean;
  canManage: boolean;
  participantStatus?: ParticipantStatus | null;
}

/**
 * Matches Aperture.Application.Competitions.Dtos.CompetitionParticipantDto
 * exactly: CompetitionParticipantDto(Guid Id, Guid CompetitionId, Guid UserId, DateTime InvitedAt,
 * ParticipantStatus Status, DateTime? RequestedAt, DateTime? DecidedAt, Guid? DecidedByUserId).
 * A competitor only sees/enters a competition once this record's Status is
 * Approved (see CompetitionsController's participants/* endpoints).
 */
export interface CompetitionParticipant {
  id: string;
  competitionId: string;
  userId: string;
  invitedAt: string;
  status: ParticipantStatus;
  requestedAt?: string | null;
  decidedAt?: string | null;
  decidedByUserId?: string | null;
  /** Entry fee received - marked by an organizer. */
  hasPaid: boolean;
  paidAt?: string | null;
  /** How it was paid; set together with hasPaid. */
  paidVia?: PaymentMethod | null;
}

/**
 * Matches Aperture.Application.Competitions.Dtos.MyCompetitionDto exactly -
 * one row per competition the calling user has any relationship to (manages,
 * judges, or has requested/been assigned to as a participant). Returned by
 * GET /users/me/competitions; powers the competition switcher and the
 * "browse to join" list.
 */
export interface MyCompetition {
  competitionId: string;
  competitionName: string;
  organizationName?: string | null;
  status: CompetitionStatus;
  isOpenForSignup: boolean;
  isParticipant: boolean;
  participantStatus?: ParticipantStatus | null;
  isJudge: boolean;
  canManage: boolean;
}

export interface Category {
  id: string;
  competitionId: string;
  name: string;
  description?: string;
  maxSubmissionsPerUser?: number;
}

/**
 * Matches Aperture.Domain.Submissions.SubmissionStatus exactly: Draft=0,
 * Submitted=1, Accepted=2, Rejected=3. Serializes as a raw numeric ordinal
 * (no JsonStringEnumConverter anywhere in this backend), same pattern as
 * CompetitionStatus/ReportType/ReportStatus.
 */
export const SubmissionStatus = {
  Draft: 0,
  Submitted: 1,
  Accepted: 2,
  Rejected: 3,
} as const;
export type SubmissionStatus = (typeof SubmissionStatus)[keyof typeof SubmissionStatus];

/**
 * Matches Aperture.Application.Submissions.Dtos.SubmissionDto exactly:
 * SubmissionDto(Guid Id, Guid CompetitionId, Guid CategoryId, Guid CompetitorUserId,
 * string Title, string? Description, SubmissionStatus Status, DateTime? SubmittedAt,
 * string? RejectionReason, DateTime CreatedAt). There is no imageUrl/thumbnailUrl/
 * averageScore/rank here - images are a separate entity (GET /images?submissionId=)
 * and average score/rank only exist on the separate SubmissionRanking DTO below.
 */
export interface Submission {
  id: string;
  competitionId: string;
  categoryId: string;
  competitorUserId: string;
  title: string;
  description?: string | null;
  status: SubmissionStatus;
  submittedAt?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
}

/**
 * Matches Aperture.Application.Media.Dtos.ImageDto exactly. There is no
 * resolved imageUrl/thumbnailUrl here - image processing (validate ->
 * metadata -> thumbnail -> watermark -> store) happens asynchronously in
 * Aperture.Workers, so a just-uploaded image only has file metadata and a
 * lifecycle `status`. Poll GET /images/{id} (or GET /images?submissionId=)
 * to see the status advance; there is still no URL field once processing
 * completes on the current backend.
 */
export const ImageStatus = {
  Uploaded: 0,
  Validating: 1,
  ValidationFailed: 2,
  MetadataExtracted: 3,
  ThumbnailGenerated: 4,
  WatermarkGenerated: 5,
  Stored: 6,
  Failed: 7,
} as const;
export type ImageStatus = (typeof ImageStatus)[keyof typeof ImageStatus];

export interface SubmissionImage {
  id: string;
  submissionId: string;
  originalFileName: string;
  contentType: string;
  fileSizeBytes: number;
  widthPx?: number | null;
  heightPx?: number | null;
  status: ImageStatus;
  failureReason?: string | null;
  /** What the image gets wrong against the competition's requirements (kept-but-flagged mode); null = fine. */
  requirementIssues?: string | null;
  processedAt?: string | null;
  createdAt: string;
  /** Presigned URLs, populated once the worker has produced that pipeline stage - null until then. */
  thumbnailUrl?: string | null;
  previewUrl?: string | null;
  watermarkedUrl?: string | null;
}

/**
 * Shape of GET /judging/my-competitions, matching the backend's
 * CompetitionDto exactly (unlike the loosely-guessed `Competition` type
 * above, this endpoint's contract was read directly from the backend).
 */
export interface JudgeCompetition {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  status: CompetitionStatus;
  submissionOpensAt?: string | null;
  submissionDeadline?: string | null;
  judgingDeadline?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
}

/**
 * Matches Aperture.Application.Judging.Dtos.SubmissionRankingDto exactly:
 * SubmissionRankingDto(Guid SubmissionId, string Title, Guid CategoryId, decimal AverageScore, int ScoreCount, int Rank).
 * Returned by GET /judging/competitions/{competitionId}/ranking.
 */
export interface SubmissionRanking {
  submissionId: string;
  title: string;
  categoryId: string;
  averageScore: number;
  scoreCount: number;
  rank: number;
  /** Selected for the exhibition (the best share of the category). */
  isSelected: boolean;
}

/** Outcome of PUT /competitions/{id}/exhibition-selection. */
export interface ExhibitionSelection {
  percent: number;
  selected: number;
  scored: number;
}

/**
 * Matches Aperture.Application.Judging.Dtos.ScoreDto exactly:
 * ScoreDto(Guid Id, Guid SubmissionId, Guid JudgeUserId, decimal Value, string? Comments, DateTime ScoredAt).
 */
export interface JudgingScore {
  id: string;
  submissionId: string;
  judgeUserId: string;
  value: number;
  comments?: string | null;
  scoredAt: string;
}

/**
 * Matches Aperture.Application.Judging.Dtos.SubmitScoreRequest exactly:
 * SubmitScoreRequest(Guid SubmissionId, decimal Value, string? Comments).
 * POST /judging/scores is an upsert (see JudgingController.SubmitScore's XML
 * doc comment) - there is no separate update endpoint, submitting again with
 * the same submissionId just replaces the calling judge's existing score.
 */
export interface SubmitScoreRequest {
  submissionId: string;
  value: number;
  comments?: string | null;
}

/**
 * Matches Aperture.Application.Judging.Dtos.JudgingProgressDto exactly:
 * JudgingProgressDto(Guid CompetitionId, int TotalToJudge, int JudgedByMe, int Remaining).
 * Returned by GET /judging/competitions/{competitionId}/progress. TotalToJudge
 * only counts Accepted submissions - the same set a judge can actually see and score.
 */
export interface JudgingProgress {
  competitionId: string;
  totalToJudge: number;
  judgedByMe: number;
  remaining: number;
}

/**
 * Matches Aperture.Application.Submissions.Dtos.CategoryProgressDto exactly:
 * CategoryProgressDto(Guid CategoryId, string CategoryName, int Uploaded, int Max, int Remaining).
 */
export interface CategoryProgress {
  categoryId: string;
  categoryName: string;
  uploaded: number;
  max: number;
  remaining: number;
}

/**
 * Matches Aperture.Application.Submissions.Dtos.SubmissionProgressDto exactly:
 * SubmissionProgressDto(Guid CompetitionId, IReadOnlyList<CategoryProgressDto> Categories,
 * int TotalUploaded, int TotalMax, int TotalRemaining).
 * Returned by GET /submissions/progress?competitionId= for the calling competitor.
 */
export interface SubmissionProgress {
  competitionId: string;
  categories: CategoryProgress[];
  totalUploaded: number;
  totalMax: number;
  totalRemaining: number;
}

/**
 * Backend enums serialize as their raw numeric ordinal (System.Text.Json
 * default - no JsonStringEnumConverter is registered), not as strings. These
 * mirror Aperture.Domain.Reports.ReportType / ReportStatus exactly so
 * equality checks against API responses actually work.
 */
export const ReportType = {
  CompetitionSummary: 0,
  JudgingResults: 1,
} as const;
export type ReportType = (typeof ReportType)[keyof typeof ReportType];

export const ReportStatus = {
  Pending: 0,
  Processing: 1,
  Completed: 2,
  Failed: 3,
} as const;
export type ReportStatus = (typeof ReportStatus)[keyof typeof ReportStatus];

export interface TriggerReportRequest {
  competitionId: string;
  type: ReportType;
}

export interface ReportRecord {
  id: string;
  competitionId: string;
  submissionId?: string | null;
  type: ReportType;
  status: ReportStatus;
  downloadUrl?: string | null;
  failureReason?: string | null;
  completedAt?: string | null;
  createdAt: string;
}

/**
 * Matches Aperture.Application.Awards.Dtos.AwardDto exactly - no enum fields,
 * so no numeric-ordinal mapping is needed here (unlike ReportType/Status).
 */
export interface Award {
  id: string;
  competitionId: string;
  categoryId?: string | null;
  submissionId: string;
  title: string;
  rank: number;
  awardTypeId?: string | null;
}

/** Gives an award definition to an entry; title, place and category come from the definition. */
export interface CreateAwardRequest {
  competitionId: string;
  submissionId: string;
  awardTypeId: string;
}

/** An award the organizers define for a competition (what can be given). */
export interface AwardType {
  id: string;
  competitionId: string;
  categoryId?: string | null;
  name: string;
  description?: string | null;
  rank: number;
  quantity: number;
}

export interface AwardTypePayload {
  categoryId?: string | null;
  name: string;
  description?: string | null;
  rank: number;
  quantity: number;
}

export interface MyResultAward {
  title: string;
  rank: number;
}

/**
 * Matches Aperture.Application.Judging.Dtos.MyResultDto - one of the caller's entries.
 * Score, places and awards are only filled in once judging has finished (resultsPublished).
 */
export interface MyResult {
  submissionId: string;
  title: string;
  competitionId: string;
  competitionName: string;
  categoryId: string;
  categoryName: string;
  status: SubmissionStatus;
  rejectionReason?: string | null;
  resultsPublished: boolean;
  averageScore?: number | null;
  scoreCount: number;
  rank?: number | null;
  rankedEntries: number;
  categoryRank?: number | null;
  categoryEntries?: number | null;
  awards: MyResultAward[];
  /** Selected for the exhibition; only true once the results are published. */
  isSelected: boolean;
}

export interface AuditLogEntry {
  id: string;
  actorId?: string;
  actorName?: string;
  actorEmail?: string;
  action: string;
  entityType?: string;
  entityId?: string;
  timestamp: string;
  details?: string;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Generic wrapper for endpoints that might respond with `{ data: T }`. */
export interface ApiEnvelope<T> {
  data: T;
}
